// Portfolio slice — the data seam for the per-plan Portfolio screen. Lives
// centrally in store/slices (one slice file per page). It always fetches the
// backend /portfolio for the selected account and maps it onto the UI shape —
// the frontend mock is gone (same move the dashboard made): fixture data lives in
// pcs-backend/backend/data/portfolio.json via scripts/generate_data.py, and the
// wire-shaped test fixture lives in src/test-utils/fixtures/portfolio.mock.js.
// The `usePortfolioData` hook (portfolio module) wraps this so the page stays
// presentational. Mappers live in portfolio.adapters (the camelCase ↔ snake_case
// boundary) — components read camelCase only.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getPortfolio } from '../../services/api/portfolio';
import { getDashboard } from '../../services/api/dashboard';
import { getManageAccount } from '../../services/api/manageAccount';
import { mapPortfolioResponse } from '../../components/modules/portfolio/portfolio.adapters';

// ----- Thunk: the backend seam -----
export const fetchPortfolio = createAsyncThunk(
  'portfolio/fetch',
  async (accountId, { rejectWithValue }) => {
    try {
      if (!accountId) return rejectWithValue('No account selected.');
      // /portfolio is the core payload; /dashboard + /manage-account enrich it with
      // summary returns, YTD-growth and the election change date (same account, but
      // owned by those screens). Enrichment calls degrade to null on failure so the
      // page still renders from /portfolio. (Compose precedent: manageAccount.slice.)
      const [data, dashboard, manage] = await Promise.all([
        getPortfolio(accountId),
        getDashboard(accountId).catch(() => null),
        getManageAccount(accountId).catch(() => null),
      ]);
      return mapPortfolioResponse(data, { dashboard, manage });
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load portfolio data');
    }
  },
);

// Starts in `loading` with no data, so the page shows its skeleton until the
// first fetch resolves — matching Dashboard/Manage on navigation. (The old
// mock-seeded shape is gone with mock mode; components already guard on the
// loading flag before reading `data`.)
const initialState = {
  data: null,
  status: 'loading', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolio.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPortfolio.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchPortfolio.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to load portfolio data';
      });
  },
});

// ----- Selectors -----
export const selectPortfolioData = (state) => state.portfolio.data;
export const selectPortfolioStatus = (state) => state.portfolio.status;
export const selectPortfolioError = (state) => state.portfolio.error;

export default portfolioSlice.reducer;
